import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { FloatingGroupOrderCart } from '../../modules/GroupRegistration/FloatingGroupOrderCart';
import { PasswordDialog } from '../passworddialog/PasswordDialog';
import { PersonPasswordGate } from '../personpasswordgate/PersonPasswordGate';
import { EmailLinkGate } from '../emaillinkgate/EmailLinkGate';
import { PageClosed } from '../PageClosed/PageClosed';
import { useMediaQuery } from '@material-ui/core';
import json2mq from 'json2mq';
import { getUrlParam } from '../../utils/url.utils';
import AnimatedBackground from '../AnimatedBackground/AnimatedBackground';
import { ValidationSnackbar } from '../validationsnackbar/ValidationSnackbar';
import { useStore } from '../../store/Store';
import { LoadSpinner } from '../loadspinner/LoadSpinner';
import EventNotPublic from '../eventnotpublic/EventNotPublic';
import SystemLabel from '../Systemlabel/SystemLabel';
import NotAuthorized from '../notauthorized/NotAuthorized';
import EventLabel from '../EventLabel/EventLabel';
import useLayoutRender from '../../hooks/useLayoutRender';
import { isFaqChild } from '../../modules/Faq/faqChildren';
import { useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import './layout.css';
import { DeadlineDue } from '../deadline-due';
import { CookieDialog } from '../../consent/CookieDialog';
import { ReopenButton } from '../../consent/ReopenButton';

const Layout = (props: any) => {
  const store = useStore();
  const modulesRefs = useRef([]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const printBodyRef = useRef<HTMLDivElement>(null);
  const contentStyleRef = useRef<any>(null);

  const location = useLocation();

  const [showPageClosed, setShowPageClosed] = useState<boolean>(store.showPageClosed);
  const [resized, setResized] = useState<number>(0);

  const { scoped = false } = props;
  const {
    hasFetched: layoutLoaded,
    ordersLoaded,
    showPwdDialog,
    showPersonPasswordGate,
    showEmailLinkGate,
    fetchData,
    fetching,
    printable,
    notAuthorized,
    mainEventDeadlinePassed,
    contentLayout,
  } = store;

  const windowOnResize = () => {
    setResized(+new Date());
  };

  useEffect(() => {
    init();
  }, [location.pathname]);

  useEffect(() => {
    if (showPageClosed !== store?.showPageClosed) {
      setShowPageClosed(store?.showPageClosed);
    }
  }, [store?.showPageClosed]);

  useEffect(() => {
    if (!scoped) init();
    let body = document.getElementsByTagName('body');
    for (const b of body) {
      b.classList.toggle('new-cms', true);
    }
    window.addEventListener('resize', windowOnResize);
    return () => {
      window.removeEventListener('resize', windowOnResize);
      let body = document.getElementsByTagName('body');
      for (const b of body) {
        b.classList.toggle('new-cms', false);
      }
    };
  }, []);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo(0, 0);
    }
  }, [store.current.activePage]);

  useEffect(() => {
    let contentStyle = `
    /*TEMPLATE*/
    ${store.current.template.formattedCss ?? ''}
    /*LAYOUT*/
    ${store.contentLayout.css ?? ''}
    /*HEADER*/
    ${store.contentHeaderLayout?.css ?? ''}
    /*FOOTER*/
    ${store.contentFooterLayout?.css ?? ''}
    `;
    const importRegex = /@import url\([\s\S]*?\);/gi;
    const importStrings = contentStyle.match(importRegex);

    if (importStrings && importStrings.length > 0) {
      importStrings.forEach(str => {
        contentStyle = str + '\r\n' + contentStyle?.replaceAll(str, '');
      });
    }
    const oldStyleEl = contentStyleRef.current;
    const newContentStyleEl = document.createElement('style');
    newContentStyleEl.id = 'THISTOO';
    newContentStyleEl.innerHTML = contentStyle;
    contentStyleRef.current = newContentStyleEl;
    document.head.appendChild(newContentStyleEl);
    if (oldStyleEl) document.head.removeChild(oldStyleEl);
  }, [store.current.template.formattedCss, store.contentLayout.css, store.contentHeaderLayout?.css, store.contentFooterLayout?.css]);

  useEffect(() => {
    if (ordersLoaded && layoutLoaded && !scoped) {
      let badgeNo = getUrlParam('badgeno');
      if (!badgeNo) badgeNo = getUrlParam('clientkey');
      // Without a badgeNo the email gate takes over instead of "link not valid".
      if (contentLayout?.onlyAuthorizedUsers && !store.showEmailLinkGate) {
        console.log(store.environment.eventId, badgeNo,  store.orders.find(o => o.badgeNo === badgeNo)?.orderRow);
        const isBookedOnMainEvent = store.orders.find(o => o.badgeNo === badgeNo)?.orderRow?.some(or => or.eventId === store.environment.eventId && (or.status.toUpperCase() === 'BOOKED' || or.status.toUpperCase() === 'CONFIRMED'));


        if (!badgeNo || !isBookedOnMainEvent) {
          runInAction(() => {
            store.notAuthorized = true;
          });
        }
      }
      if (contentLayout?.groupLeaderChangeLock) {
        const currentOrder = store.orders.find(x => x.badgeNo == badgeNo);
        if (currentOrder && currentOrder.orderGroupId && currentOrder.orderGroupId !== currentOrder.orderId) {
          runInAction(() => {
            store.notAuthorized = true;
          });
        }
      }
    }
  }, [ordersLoaded, layoutLoaded]);

  const setShowPwdDialog = (bool: boolean) => {
    runInAction(() => {
      store.showPwdDialog = bool;
    });
  };

  const init = () => {
    const layoutCode = window.location.pathname.split('/').reverse()[0];
    const prevStateNo = undefined;
    if (scoped) return;
    store.fetchData(layoutCode, prevStateNo === undefined);
  };

  const handlePrint = useReactToPrint({
    content: () => printBodyRef.current,
    onBeforeGetContent: () => {
      printBodyRef.current = bodyRef.current.cloneNode(true) as HTMLDivElement;
      printBodyRef.current.style.maxHeight = 'none';
      printBodyRef.current.style.overflow = 'visible';
    },
  });

  const isSmartphone = useMediaQuery(
    json2mq({
      maxWidth: 768,
    })
  );

  const layout = store.contentLayout?.contentLayoutModules?.filter(clm => clm.pages === store.current.activePage && !isFaqChild(clm)) ?? [];
  const headerLayout = store.contentHeaderLayout?.contentLayoutModules ?? [];
  const footerLayout = store.contentFooterLayout?.contentLayoutModules ?? [];

  const headerModules = useLayoutRender({
    ...props,
    ...store,
    layout: headerLayout,
    xrayMode: store.xrayMode,
    modulesRefs,
    isSmartphone,
    bodyWidth: bodyRef.current?.clientWidth || window.innerWidth,
  });

  const layoutModules = useLayoutRender({
    ...props,
    ...store,
    layout,
    xrayMode: store.xrayMode,
    modulesRefs,
    isSmartphone,
    bodyWidth: bodyRef.current?.clientWidth || window.innerWidth,
  });

  const footerModules = useLayoutRender({
    ...props,
    ...store,
    layout: footerLayout,
    xrayMode: store.xrayMode,
    modulesRefs,
    isSmartphone,
    bodyWidth: bodyRef.current?.clientWidth || window.innerWidth,
  });

  if (notAuthorized) {
    return (
      <NotAuthorized>
        <h1 style={{ fontFamily: 'Be Vietnam' }}>
          <SystemLabel name="TheLinkIsNotValid" />
        </h1>
        <h4 style={{ fontFamily: 'Be Vietnam', maxWidth: 500 }}>
          <SystemLabel name="TheLinkYouProvideIsNotValid" />
        </h4>
      </NotAuthorized>
    );
  }

  if (store.eventNotPublic) {
    return (
      <EventNotPublic
        layoutCode={window.location.pathname.split('/').reverse()[0]}
        publishAt={store.publishAt}
        secondsUntilPublish={store.secondsUntilPublish}
      />
    );
  }

  if (!layoutLoaded || store?.redirectingToClosedUrl || (showPageClosed && store?.contentLayout?.redirectUrl)) return <LoadSpinner />;

  if (mainEventDeadlinePassed) {
    return <DeadlineDue />;
  }

  return (
    <div className="w-screen h-full">
      <CookieDialog />
      <ReopenButton />
      {printable && (
        <button
          className="absolute bottom-4 z-30 right-8 text-sm bg-neutral-700 text-white font-medium outline-0 border border-neutral-700 flex gap-x-1  rounded-md px-2 py-1"
          onClick={handlePrint}
        >
          <i className="ri-printer-line"></i>
        </button>
      )}
      {!showPageClosed && showPwdDialog ? (
        <PasswordDialog onSuccess={() => setShowPwdDialog(false)} pwd={store.contentLayout.password} contentId={store.contentLayout.contentLayoutId} />
      ) : null}
      {!showPageClosed && showPersonPasswordGate ? (
        <PersonPasswordGate />
      ) : null}
      {!showPageClosed && showEmailLinkGate ? (
        <EmailLinkGate />
      ) : null}
      {!showPageClosed && !showPwdDialog && store?.contentLayout?.contentLayoutModules?.find(cl => cl.contentModule.type == 'GROUP_REGISTRATION') ? (
        <FloatingGroupOrderCart {...store} />
      ) : null}
      <div ref={bodyRef} className={(isSmartphone ? 'mobile ' : '') + 'body'} id="body" style={{ height: isSmartphone ? 'calc(100% + 50x)' : '100%' }}>
        <AnimatedBackground body={bodyRef.current} />
        <ValidationSnackbar {...{ ...store, modulesRefs }} />

        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }} id="test-g">
          {(() => {
            const badgeNo = getUrlParam('badgeno') || getUrlParam('clientkey');
            const bypassClosed = contentLayout?.allowChangeLinkWhenClosed && badgeNo;
            return showPageClosed || (!bypassClosed && (store.mainEventFullbooked || store.selectedActivitiesFullbooked));
          })() ? (
            <PageClosed
              openDateTime={store.contentLayout.openDateTime}
              closeDateTime={store.contentLayout.closeDateTime}
              onOpenPage={() => setShowPageClosed(false)}
            >
              <h1 style={{ fontFamily: 'Be Vietnam' }}>
                <EventLabel name="PageClosedTitle" groupName="CMS.PageClosed" />
              </h1>
              <h4 style={{ fontFamily: 'Be Vietnam', maxWidth: 500 }}>
                <EventLabel name="PageClosedBody" groupName="CMS.PageClosed" />
              </h4>
            </PageClosed>
          ) : !showPwdDialog && !showPersonPasswordGate && !showEmailLinkGate ? (
            <>
              <div style={{ position: 'relative' }}>
                <div className="cms-background-block"></div>
                {headerModules}
              </div>
              <div style={{ position: 'relative' }}>
                <div className="cms-background-block"></div>
                {layoutModules}
              </div>
              <div style={{ position: 'relative' }}>
                <div className="cms-background-block"></div>
                {footerModules}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default observer(Layout);
